import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@chakra-ui/core';
import { connect, useSelector } from 'react-redux';
import { css } from 'emotion';
import { bindActionCreators } from 'redux';
import { Formik } from 'formik';
import { BaseTabs, TabList, TabWithBadge, TabPanels } from '../common/components/Tabs';
import routes, { getQuotesPath } from '../common/routes';
import { history } from '../common/helpers/history';
import { TabPanel, QUOTES_TYPES, TypeTag, TBody } from './components';
import { quotesActions } from '../redux/quotes/actions';
import { quoteReference } from '../common/helpers/quoteReference';
import { is } from '../roleBasedAccess/Is';
import { usersAPI } from '../common/api/users';
import { foldersAPI } from '../common/api/folders';
import { companiesAPI } from '../common/api/companies';
import { departmentsAPI } from '../common/api/departments';
import { encodeQueryArray } from '../common/utils';
import { Label, SearchInput } from '../common/components/Form/FormInputs';
import { SelectInput, SelectInputMultiple } from '../common/components/Form/FormSelectInput';
import { PrimaryButton, SecondaryButton } from '../common/components/Buttons';
import { Table, TBodyRow, TD, TH, Thead, THeadRow } from '../common/components/Table';
import { Funders } from '../common/components/Funders/Funders';
import { Pagination } from '../common/components/Pagination';
import QuoteProgressSegments from './QuoteProgressSegments';
import ActionAge from '../common/components/ActionAge';
import DashboardStageTimeline from './DashboardStageTimeline';
import { useOperatorFilter } from '../common/contexts/OperatorFilterContext';

const quoteGridTemplateColumns =
  'minmax(130px, 0.6fr) 85px minmax(140px, 0.6fr) minmax(100px, 0.4fr) minmax(220px, 1fr) 90px minmax(150px, 0.7fr)';
const quoteTagColumn = ' minmax(120px, 0.5fr)';
const quoteTableMinWidth = '980px';
const DISCARDED_QUOTES_HELP =
  'Proposition sans suite, ou entreprise retenue sur aucun lot une fois le montant total du projet renseigné. Un dossier encore suivi par une autre entreprise de votre périmètre reste compté parmi les dossiers suivis.';

export const SORT_OPTIONS = [
  { label: 'Dossiers les plus récents', value: 'folder_number_desc' },
  { label: 'Dossiers les plus anciens', value: 'folder_number_asc' },
  { label: 'Demandes de devis récentes', value: 'request_desc' },
  { label: 'Demandes de devis anciennes', value: 'request_asc' },
];

const sortOption = value =>
  SORT_OPTIONS.find(option => option.value === (value?.value || value)) || SORT_OPTIONS[0];

const DEFAULT_SEARCH = {
  sort: SORT_OPTIONS[0].value,
  keywords: '',
  delay: '',
  tags: [],
  stages: [],
  characteristics: [],
  company: null,
  department: null,
};

const CHARACTERISTIC_FILTERS = {
  'Devis manquant': 'quote_required',
  'Dépôts effectués': 'deposits_validated',
  'Docs admin manquants': 'administrative_documents_missing',
  'Docs entreprise manquants': 'company_documents_missing',
  'Fonds débloqués': 'funds_released',
};

const STAGE_FILTERS = [
  'Diagnostic à domicile',
  'Constitution et validation du projet',
  'Instruction financière',
  'Suivi des travaux et solde',
  'Aboutissement du projet',
];
const DASHBOARD_PHASES = [
  'Diagnostic à domicile',
  'Constitution et validation du projet',
  'Instruction financière',
  'Suivi de fin de projet',
  'Fin de l’accompagnement',
];
const STAGE_FILTER_OPTIONS = STAGE_FILTERS.map((stage, index) => `${index + 1}. ${stage}`);
const stageFilterValue = value => value.replace(/^\d+\.\s/, '');
const COMPLETED_STAGE = 'Aboutissement du projet';
const noop = () => null;

const dashboardQueryKey = (search, isFilterDisabled) =>
  JSON.stringify({
    keywords: search.keywords || '',
    delay: search.delay || '',
    company: search.company?.value || search.companyId || '',
    department: search.department?.value || search.department || '',
    tags: search.tags || [],
    stages: search.stages || [],
    characteristics: search.characteristics || [],
    isFilterDisabled,
  });

export const quotesPathForStageSearch = stages => {
  if (stages.length === 0) return null;
  if (!stages.includes(COMPLETED_STAGE)) return getQuotesPath(0);
  return getQuotesPath(stages.length === 1 ? 2 : 4);
};
const DELAY_LABELS = {
  openingToVisit: 'Ouverture → visite',
  visitToDeposit: 'Visite → dépôt',
  depositToAnahReturn: 'Dépôt → retour ANAH',
  anahReturnToWorksRealized: 'Retour ANAH → fin de projet',
};

const DELAY_SOURCES = {
  'Ouverture → visite':
    'Date de positionnement (ou de demande de devis) et date de visite de l’entreprise.',
  'Visite → dépôt':
    'Date de visite de l’entreprise et date de validation du dépôt auprès du financeur retenu (MaPrimeAdapt’ ou ANAH).',
  'Dépôt → retour ANAH':
    'Date de validation du dépôt et date de réception de la réponse du financeur retenu (MaPrimeAdapt’ ou ANAH).',
  'Retour ANAH → fin de projet':
    'Date de réception de la réponse du financeur retenu (MaPrimeAdapt’ ou ANAH) et date de fin de projet renseignée par l’entreprise.',
};

const TOOLTIP_STYLES = {
  bg: 'darkGray',
  color: 'white',
  px: 3,
  py: 2,
  lineHeight: '1.5',
  maxW: 'min(320px, calc(100vw - 32px))',
  borderRadius: 'md',
  zIndex: 'tooltip',
  hasArrow: true,
};

const numberOrDash = value => (value === null || value === undefined ? '—' : value);

const expectedFoldersDescription = count => {
  if (count === undefined) return undefined;
  return `sur ${count} dossier${count > 1 ? 's' : ''}`;
};

const formatDueDate = value => {
  const parts = String(value || '').split('-');
  if (parts.length !== 3) return null;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const expectedMetricDescription = ({ folderCount, nextDueAt }) =>
  [
    expectedFoldersDescription(folderCount),
    formatDueDate(nextDueAt) ? `Prochaine échéance : ${formatDueDate(nextDueAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

const getDashboardMeta = meta => meta?.dashboard || meta || {};

const findMetric = (source, keys) => {
  if (!source) return undefined;
  return keys.reduce((value, key) => (value === undefined ? source[key] : value), undefined);
};

const metricEntries = (source, labels) => {
  if (Array.isArray(source)) {
    return source.map(item => ({
      key: item.key,
      label:
        item.label ||
        item.name ||
        DELAY_LABELS[item.stage] ||
        DELAY_LABELS[item.key] ||
        item.stage ||
        item.key,
      value: item.value ?? item.count ?? item.total ?? item.days,
      folderCount: item.folderCount,
      sampleSize: item.sampleSize,
      nextDueAt: item.nextDueAt,
    }));
  }

  if (source && typeof source === 'object') {
    return Object.entries(source).map(([label, value]) => ({
      key: label,
      label:
        value && typeof value === 'object'
          ? value.label || value.name || DELAY_LABELS[label] || label
          : DELAY_LABELS[label] || label,
      value:
        value && typeof value === 'object'
          ? value.value ?? value.count ?? value.total ?? value.days
          : value,
      sampleSize: value?.sampleSize,
    }));
  }

  return labels.map(label => ({ label, value: undefined }));
};

const dashboardPlaceholder = {
  dashboard: {
    expectedElementsTotal: '—',
    expectedFoldersCount: '—',
    expectedElements: [
      ['quote_required', 'Devis manquant'],
      ['administrative_documents_missing', 'Docs admin manquants'],
      ['company_documents_missing', 'Docs entreprise manquants'],
    ].map(([key, label]) => ({ key, label, value: '—', folderCount: '—' })),
    averageDelays: Object.entries(DELAY_LABELS).map(([key, label]) => ({
      key,
      label,
      value: '—',
      sampleSize: 1,
    })),
    operationalKpis: [
      ['total_folders', 'Nombre total de dossiers'],
      ['quotes_expected', 'Dossiers en attente de devis', '/demandes-de-devis/dossiers-actifs'],
      ['deposited_awaiting_works', 'Dossiers déposés en attente de travaux'],
      ['works_in_progress', 'Travaux en cours'],
      ['balance_request_expected', 'Dossiers en attente de demande de solde'],
      ['balance_payment_expected', 'Dossiers en attente de versement du solde'],
      ['projects_completed', 'Projets réalisés'],
    ].map(([key, label, path]) => ({ key, label, path })),
    projectPlaceKpis: [
      ['project_place_folders', 'Dossiers disponibles sur la Project Place', '/project-place'],
      ['project_place_departments', 'Départements disponibles'],
      ['project_place_companies', 'Entreprises disponibles'],
    ].map(([key, label, path]) => ({ key, label, path })),
  },
};

const dashboardPlaceholderStyle = css({
  filter: 'grayscale(1)',
  opacity: 0.7,
  '& *': { color: 'transparent !important', animation: 'none !important' },
  '& svg': { visibility: 'hidden' },
  '& p, & h1, & h2': { backgroundColor: '#DDE2E8', borderRadius: '4px' },
});

const DashboardHeader = ({ children }) => (
  <Box bg="darkGray" color="white" rounded="lg" px={{ base: 4, md: 5 }} py={4} mb={3}>
    <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={4}>
      <Box>
        <Text
          color="coral.900"
          fontSize="xs"
          fontWeight="bold"
          letterSpacing="0.08em"
          textTransform="uppercase"
        >
          Suivi des devis
        </Text>
        <Heading as="h1" mt={1} fontSize="2xl" color="white">
          Tableau de bord entreprise
        </Heading>
        <Text mt={1} color="rgba(255, 255, 255, 0.72)" fontSize="sm">
          Vos dossiers, vos échéances et les actions attendues au même endroit
        </Text>
      </Box>
      {children}
    </Flex>
  </Box>
);

export const QuoteDashboardSummary = ({
  meta,
  pending = false,
  error = false,
  onRetry,
  onExpectedQuotes,
  onExpectedAction,
  placeholder = false,
}) => {
  if (pending || error || !meta) {
    const failed = error && !pending;

    return (
      <Box mb={5} data-testid="quote-dashboard-summary" aria-busy={!failed}>
        <DashboardHeader>
          <Flex alignItems="center" flexWrap="wrap" gridGap={3} role={failed ? 'alert' : 'status'}>
            {!failed && (
              <Spinner color="coral.700" emptyColor="semiDarkGray.600" aria-hidden="true" />
            )}
            <Text color="white">
              {failed
                ? 'Impossible de charger le tableau de bord.'
                : 'Chargement du tableau de bord…'}
            </Text>
            {failed && onRetry && (
              <SecondaryButton accessibleCoral onClick={onRetry}>
                Réessayer
              </SecondaryButton>
            )}
          </Flex>
        </DashboardHeader>
        <Box
          aria-hidden="true"
          inert=""
          pointerEvents="none"
          className={dashboardPlaceholderStyle}
          data-testid="quote-dashboard-placeholder"
        >
          <QuoteDashboardSummary meta={dashboardPlaceholder} placeholder />
        </Box>
      </Box>
    );
  }

  const dashboard = getDashboardMeta(meta);
  const topLevel = meta || {};
  const stageSource = findMetric(dashboard, ['folderPhaseCounts', 'stages', 'stageCounts']);
  const delaySource = findMetric(dashboard, ['averageDelays', 'delays', 'averageDelayByStage']);
  const expectedSource = findMetric(dashboard, [
    'expectedElements',
    'expectedCounts',
    'expectedElementsCount',
    'expected',
    'itemsExpected',
  ]);
  const rawStages = metricEntries(stageSource, DASHBOARD_PHASES);
  const stageEntries = STAGE_FILTERS.map((label, index) => ({
    label,
    value: rawStages.find(entry => [label, DASHBOARD_PHASES[index]].includes(entry.label))?.value,
  }));
  const delayEntries = metricEntries(delaySource, []);
  const expectedEntries = metricEntries(expectedSource, []);
  const operationalKpis = dashboard.operationalKpis || [];
  const projectPlaceKpis = dashboard.projectPlaceKpis || [];
  const calculatedExpectedTotal = expectedEntries.reduce((total, entry) => {
    const value = Number(entry.value);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
  const expectedTotal = dashboard.expectedElementsTotal ?? calculatedExpectedTotal;
  const { expectedFoldersCount } = dashboard;
  const statusEntries = [
    {
      label: 'Dossiers actifs',
      value: topLevel.activeCount !== undefined ? topLevel.activeCount : dashboard.activeCount,
    },
    {
      label: 'Dossiers en instance',
      value: topLevel.pendingCount !== undefined ? topLevel.pendingCount : dashboard.pendingCount,
    },
    {
      label: 'Dossiers clôturés',
      value: topLevel.closedCount !== undefined ? topLevel.closedCount : dashboard.closedCount,
    },
    {
      label: 'Dossiers écartés',
      value:
        topLevel.discardedCount !== undefined ? topLevel.discardedCount : dashboard.discardedCount,
    },
  ];

  return (
    <Box mb={placeholder ? 0 : 5} data-testid={placeholder ? undefined : 'quote-dashboard-summary'}>
      {!placeholder && (
        <DashboardHeader>
          <Flex flexWrap="wrap" gridGap={2}>
            {statusEntries.map(({ label, value }) => (
              <StatusPill key={label} label={label} value={numberOrDash(value)} />
            ))}
          </Flex>
        </DashboardHeader>
      )}
      <Box
        display="grid"
        gridTemplateColumns={{ base: '1fr', xl: 'minmax(0, 2fr) minmax(260px, 0.75fr)' }}
        gridGap={3}
      >
        <Box
          bg="white"
          display="flex"
          flexDirection="column"
          rounded="lg"
          boxShadow="0 4px 10px rgba(46, 73, 112, 0.1)"
          px={{ base: 4, md: 5 }}
          py={4}
        >
          <Flex justifyContent="space-between" alignItems="baseline" mb={3} flexWrap="wrap">
            <Heading as="h2" fontSize="md" color="semiDarkGray.900">
              Dossiers par étape
            </Heading>
            <Text color="semiDarkGray.600" fontSize="xs">
              Chaque dossier est compté une seule fois, hors dossiers écartés.
            </Text>
          </Flex>
          <DashboardStageTimeline stages={stageEntries} />
          {dashboard.foldersWithoutPhaseCount > 0 && (
            <Text mt={3} fontSize="xs" color="semiDarkGray.600">
              {dashboard.foldersWithoutPhaseCount} dossier(s) supplémentaire(s) sans étape
              renseignée.
            </Text>
          )}
        </Box>
        <Box
          bg="select.bg.isSelected"
          border="1px solid"
          borderColor="rgba(249, 97, 97, 0.24)"
          rounded="lg"
          px={5}
          py={4}
        >
          <Text color="red" fontSize="xs" fontWeight="bold" textTransform="uppercase">
            BelAvie attend de vous
          </Text>
          <Flex alignItems="baseline" mt={1}>
            <Text color="red" fontFamily="heading" fontSize="3xl" fontWeight="bold">
              {expectedTotal}
            </Text>
            <Text ml={2} color="semiDarkGray.900" fontSize="sm" fontWeight="semibold">
              élément{expectedTotal > 1 ? 's' : ''} à traiter
            </Text>
          </Flex>
          <Text color="semiDarkGray.900" fontSize="xs">
            {expectedFoldersDescription(expectedFoldersCount)} actif
            {expectedFoldersCount > 1 ? 's' : ''} concerné{expectedFoldersCount > 1 ? 's' : ''}
          </Text>
          <Text mt={2} fontSize="xs" color="semiDarkGray.900">
            Un dossier peut cumuler plusieurs devis et documents attendus. Les documents
            administratifs sont comptés une seule fois par dossier.
          </Text>
          <Box mt={3} pt={2} borderTop="1px solid" borderColor="rgba(249, 97, 97, 0.2)">
            {expectedEntries.map(entry => (
              <Flex
                key={`expected-${entry.label}`}
                as="button"
                type="button"
                w="100%"
                textAlign="left"
                aria-label={`Voir les dossiers : ${entry.label}`}
                onClick={() => onExpectedAction?.(entry.key)}
                cursor="pointer"
                rounded="sm"
                _hover={{ bg: 'rgba(249, 97, 97, 0.12)' }}
                _focus={{ outline: '2px solid #B33131', outlineOffset: '2px' }}
                justifyContent="space-between"
                alignItems="flex-start"
                gridGap={3}
                py={1}
              >
                <Box>
                  <Text fontSize="sm" fontWeight="semibold">
                    {entry.label}
                  </Text>
                  <Text color="semiDarkGray.900" fontSize="xs" minH="36px">
                    {expectedMetricDescription(entry)}
                  </Text>
                </Box>
                <Text color="red" fontSize="lg" fontWeight="bold">
                  {numberOrDash(entry.value)}
                </Text>
              </Flex>
            ))}
          </Box>
        </Box>
      </Box>
      {(operationalKpis.length > 0 || projectPlaceKpis.length > 0) && (
        <Box mt={3}>
          <Heading as="h2" fontSize="sm" color="semiDarkGray.900" mb={2}>
            Indicateurs de pilotage
          </Heading>
          <Box
            display="grid"
            gridTemplateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
            gridGap={3}
          >
            {operationalKpis.map(kpi => (
              <DashboardKpiCard
                key={kpi.key}
                {...kpi}
                onOpen={
                  ['quotes_expected', 'quotesExpected'].includes(kpi.key)
                    ? onExpectedQuotes
                    : undefined
                }
              />
            ))}
          </Box>
          {projectPlaceKpis.length > 0 && (
            <Box
              mt={3}
              bg="select.bg.isSelected"
              border="1px solid"
              borderColor="rgba(249, 97, 97, 0.2)"
              rounded="lg"
              p={4}
            >
              <Flex alignItems="baseline" justifyContent="space-between" mb={3} flexWrap="wrap">
                <Heading as="h2" fontSize="sm" color="semiDarkGray.900">
                  Project Place
                </Heading>
                <Text color="semiDarkGray.900" fontSize="xs">
                  Opportunités actuellement disponibles
                </Text>
              </Flex>
              <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                gridGap={3}
              >
                {projectPlaceKpis.map(kpi => (
                  <DashboardKpiCard key={kpi.key} {...kpi} accent />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
      <Box mt={3}>
        <Flex alignItems="baseline" justifyContent="space-between" mb={2}>
          <Heading as="h2" fontSize="sm" color="semiDarkGray.900">
            {dashboard.totalFolders === 1 ? 'Délais du dossier sélectionné' : 'Délais moyens'}
          </Heading>
          <Text color="semiDarkGray.900" fontSize="xs">
            en jours ouvrés
          </Text>
        </Flex>
        <Text color="semiDarkGray.600" fontSize="xs" mb={3}>
          Moyennes calculées sur les dates renseignées. La visite est celle de l’entreprise.
        </Text>
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
          gridGap={3}
        >
          {delayEntries.map(({ label, value, sampleSize }) => (
            <DelayCard
              key={`delay-${label}`}
              label={label}
              value={numberOrDash(value)}
              sampleSize={sampleSize}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const DashboardKpiCard = ({ label, count, path, accent = false, onOpen }) => {
  const openPath = event => {
    if (!path) return;

    event.preventDefault();
    if (onOpen) onOpen();
    else history.push(path);
  };

  return (
    <Box
      as={path ? 'a' : 'div'}
      href={path}
      onClick={openPath}
      bg="white"
      border="1px solid"
      borderColor={accent ? 'rgba(249, 97, 97, 0.28)' : 'lightGray3'}
      rounded="lg"
      px={4}
      py={3}
      color="inherit"
      textDecoration="none"
      cursor={path ? 'pointer' : 'default'}
      transition="transform 150ms ease, box-shadow 150ms ease"
      _hover={path ? { transform: 'translateY(-2px)', boxShadow: 'md' } : undefined}
      _focus={path ? { boxShadow: 'outline' } : undefined}
    >
      <Flex alignItems="flex-start" justifyContent="space-between" gridGap={3}>
        <Text color="semiDarkGray.900" fontSize="xs" fontWeight="semibold" lineHeight="1.4">
          {label}
        </Text>
        <Text
          flexShrink={0}
          color={accent ? 'red' : 'semiDarkGray.900'}
          fontFamily="heading"
          fontSize="2xl"
          fontWeight="bold"
        >
          {numberOrDash(count)}
        </Text>
      </Flex>
      {path && (
        <Text mt={2} color="red" fontSize="xs" fontWeight="bold">
          Voir les dossiers →
        </Text>
      )}
    </Box>
  );
};

const StatusPill = ({ label, value }) => (
  <Flex
    alignItems="center"
    bg="rgba(255, 255, 255, 0.1)"
    border="1px solid"
    borderColor="rgba(255, 255, 255, 0.14)"
    rounded="full"
    px={3}
    py={2}
  >
    <Text fontFamily="heading" fontSize="lg" fontWeight="bold" color="white">
      {value}
    </Text>
    <Text ml={2} color="rgba(255, 255, 255, 0.76)" fontSize="xs">
      {label}
    </Text>
  </Flex>
);

const DiscardedQuotesTab = React.forwardRef((props, ref) => {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <Flex position="relative" role="presentation" flexShrink={0}>
      <TabWithBadge {...props} ref={ref} pr={12} />
      <Tooltip isOpen={helpOpen} label={DISCARDED_QUOTES_HELP} placement="top" {...TOOLTIP_STYLES}>
        <Button
          type="button"
          variant="unstyled"
          variantColor="neutral"
          position="absolute"
          right={3}
          top="17px"
          bottom={1}
          minW={6}
          w={6}
          h="auto"
          zIndex={11}
          color="semiDarkGray.900"
          aria-label="Comprendre les dossiers écartés"
          _focus={{ outline: '2px solid #B33131', outlineOffset: '2px' }}
          onMouseEnter={() => setHelpOpen(true)}
          onMouseLeave={event => {
            if (document.activeElement !== event.currentTarget) setHelpOpen(false);
          }}
          onFocus={() => setHelpOpen(true)}
          onBlur={() => setHelpOpen(false)}
          onClick={event => {
            event.currentTarget.focus();
            setHelpOpen(true);
          }}
          onKeyDown={event => {
            event.stopPropagation();
            if (event.key === 'Escape') {
              setHelpOpen(false);
              event.currentTarget.blur();
            }
          }}
        >
          <Icon name="info-outline" aria-hidden="true" />
        </Button>
      </Tooltip>
    </Flex>
  );
});

const DelayCard = ({ label, value, sampleSize }) => (
  <Flex
    bg="white"
    rounded="lg"
    border="1px solid"
    borderColor="lightGray3"
    px={4}
    py={3}
    alignItems="center"
    justifyContent="space-between"
    gridGap={3}
  >
    <Box minW={0}>
      <Flex alignItems="center" gridGap={2}>
        <Text color="semiDarkGray.900" fontSize="xs" fontWeight="semibold">
          {label}
        </Text>
        {DELAY_SOURCES[label] && (
          <Tooltip
            label={`Dates nécessaires : ${DELAY_SOURCES[label]} Seuls les intervalles complets et chronologiques sont pris en compte.`}
            placement="top"
            {...TOOLTIP_STYLES}
          >
            <Button
              type="button"
              variant="unstyled"
              variantColor="neutral"
              minW={5}
              h={5}
              flexShrink={0}
              color="semiDarkGray.900"
              aria-label={`Comprendre le délai ${label}`}
            >
              <Icon name="info-outline" aria-hidden="true" />
            </Button>
          </Tooltip>
        )}
      </Flex>
      {sampleSize !== undefined && (
        <Text mt={1} color="semiDarkGray.600" fontSize="xs">
          {sampleSize === 0
            ? 'Aucun délai calculable'
            : `${sampleSize} intervalle${sampleSize > 1 ? 's' : ''} renseigné${
                sampleSize > 1 ? 's' : ''
              }`}
        </Text>
      )}
    </Box>
    <Text
      flexShrink={0}
      color="semiDarkGray.900"
      fontFamily="heading"
      fontSize="lg"
      fontWeight="bold"
    >
      {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
      {value !== '—' ? (
        <Text as="span" ml={1} color="semiDarkGray.900" fontSize="xs" fontWeight="normal">
          j
        </Text>
      ) : null}
    </Text>
  </Flex>
);

export const statusVariant = status => {
  if (status === 'En instance') return 'orange';
  if (status === 'Clôturé' || status === 'Clôture prématurée') return 'gray';
  if (status === 'Devis écarté') return 'black';
  return 'green';
};

const QuoteMilestones = ({ quote }) => {
  return (
    <Flex justifyContent="center" alignItems="center" gridGap={2} role="group" aria-label="Jalons">
      {[
        {
          icon: 'tick',
          label: 'Validation des dépôts effectuée',
          visible: quote.depositsValidated || quote.fundsReleased,
        },
        { icon: 'euro', label: 'Fonds reçus', visible: quote.fundsReleased },
      ].map(({ icon, label, visible }) => (
        <Tooltip
          key={icon}
          label={label}
          hasArrow
          bg="semiDarkGray.900"
          color="white"
          placement="top"
          zIndex={1000}
        >
          <Flex
            as="span"
            role={visible ? 'img' : undefined}
            aria-label={visible ? label : undefined}
            visibility={visible ? 'visible' : 'hidden'}
            aria-hidden={!visible}
            tabIndex={visible ? 0 : -1}
            width="24px"
            height="24px"
            alignItems="center"
            justifyContent="center"
            cursor="help"
            rounded="sm"
            _focus={{ outline: '2px solid #B33131', outlineOffset: '2px' }}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
          >
            <Icon name={icon} color="panel.info.text" aria-hidden="true" />
          </Flex>
        </Tooltip>
      ))}
    </Flex>
  );
};

const quoteActionTooltip = (action, detail, reference) => {
  if (detail?.modificationRequest?.comment)
    return `Modifications demandées : ${detail.modificationRequest.comment}`;
  if (reference && action === 'Devis modifié à déposer')
    return `Modification demandée : ${reference}`;
  if (reference && action === 'Devis initial à déposer') return `Devis attendu : ${reference}`;
  return `Traiter : ${action}`;
};

const QuoteActions = ({ actions, details = [], onAction }) => (
  <Flex flexDirection="column" alignItems="flex-start" gridGap={3}>
    {actions.map(action => {
      const detail = details.find(item => item.label === action);
      const reference = quoteReference(detail);
      const tooltipLabel = (
        <Box>
          {detail?.documents?.join(' · ') || quoteActionTooltip(action, detail, reference)}
          <ActionAge detail={detail} showDate color="inherit" />
        </Box>
      );
      return (
        <Tooltip key={action} label={tooltipLabel} {...TOOLTIP_STYLES}>
          <Button
            type="button"
            variant="link"
            variantColor="neutral"
            size="sm"
            leftIcon="info-outline"
            color="coral.700"
            whiteSpace="normal"
            textAlign="left"
            aria-label={`Traiter : ${action}`}
            onClick={event => {
              event.stopPropagation();
              onAction(action, event);
            }}
            onKeyDown={event => event.stopPropagation()}
          >
            <Box as="span">
              {action}
              {reference && (
                <Text
                  as="span"
                  display="block"
                  fontSize="xs"
                  fontWeight="semibold"
                  color="semiDarkGray.900"
                >
                  {reference}
                </Text>
              )}
              <ActionAge detail={detail} />
            </Box>
          </Button>
        </Tooltip>
      );
    })}
  </Flex>
);

export const QuotesTabs = ({
  match: {
    params: { quotesType },
  },
  quotes,
  pending,
  error,
  getQuotes,
  getDashboard = noop,
  updateQuotesComments,
  meta,
  dashboardMeta,
  dashboardPending = false,
  dashboardError = false,
}) => {
  const { isFilterDisabled } = useOperatorFilter();
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState(() => ({
    ...DEFAULT_SEARCH,
    characteristics: Object.values(CHARACTERISTIC_FILTERS).includes(
      new URLSearchParams(history.location.search).get('characteristic'),
    )
      ? [new URLSearchParams(history.location.search).get('characteristic')]
      : [],
  }));
  const queryRef = useRef({ quotesType, search });
  const dashboardQueryRef = useRef(null);
  const dashboardGenerationRef = useRef(0);
  const dashboardActionRef = useRef(getDashboard);
  const hasDashboardAction = getDashboard !== noop;
  const [dashboardWaiting, setDashboardWaiting] = useState(hasDashboardAction);
  const mountedRef = useRef(true);
  dashboardActionRef.current = getDashboard;

  useEffect(
    () => () => {
      mountedRef.current = false;
      dashboardGenerationRef.current += 1;
    },
    [],
  );

  const applySearch = nextSearch => {
    setSearch(nextSearch);
    const params = new URLSearchParams(history.location.search);
    if (
      params.has('characteristic') &&
      !nextSearch.characteristics.includes(params.get('characteristic'))
    ) {
      params.delete('characteristic');
      history.replace({ ...history.location, search: params.toString() });
    }
  };

  const showExpectedAction = characteristic => {
    if (!Object.values(CHARACTERISTIC_FILTERS).includes(characteristic)) return;
    setCurrentPage(1);
    setSearch(previous => ({ ...previous, characteristics: [characteristic] }));
    history.push(`${getQuotesPath(0)}?characteristic=${characteristic}`);
    const results = document.getElementById('quote-results');
    if (results && results.scrollIntoView) {
      results.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const queryChanged =
      queryRef.current.quotesType !== quotesType || queryRef.current.search !== search;
    queryRef.current = { quotesType, search };

    if (queryChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    const request = getQuotes(currentPage, { ...search, status: quotesType }, isFilterDisabled);
    const queryKey = dashboardQueryKey(search, isFilterDisabled);

    if (!hasDashboardAction) return;
    if (dashboardQueryRef.current === queryKey) return;

    dashboardQueryRef.current = queryKey;
    dashboardGenerationRef.current += 1;
    const generation = dashboardGenerationRef.current;
    const dashboardSearch = search;
    setDashboardWaiting(true);

    const loadDashboard = () => {
      if (
        !mountedRef.current ||
        generation !== dashboardGenerationRef.current ||
        dashboardQueryRef.current !== queryKey
      ) {
        return;
      }

      dashboardActionRef.current(dashboardSearch, isFilterDisabled);
      setDashboardWaiting(false);
    };

    Promise.resolve(request).then(loadDashboard, loadDashboard);
  }, [getQuotes, quotesType, search, currentPage, isFilterDisabled, hasDashboardAction]);

  if (!quotes) return null;

  const tabIndex = routes.quotes.types.indexOf(`/${quotesType}`);
  const queryKey = dashboardQueryKey(search, isFilterDisabled);
  const waitingForDashboard =
    hasDashboardAction && (dashboardWaiting || dashboardQueryRef.current !== queryKey);
  const dashboardIsPending = waitingForDashboard || dashboardPending;
  const loadedSummaryMeta = dashboardMeta === undefined ? meta : dashboardMeta;
  const summaryMeta = dashboardIsPending || dashboardError ? null : loadedSummaryMeta;
  const countsMeta = dashboardIsPending || dashboardError ? null : dashboardMeta || meta;

  function metaCountByType(type) {
    if (!countsMeta) {
      return null;
    }

    switch (type) {
      case 'green':
        return countsMeta.activeCount;
      case 'orange':
        return countsMeta.pendingCount;
      case 'red':
        return countsMeta.closedCount;
      case 'black':
        return countsMeta.discardedCount;
      default:
        return countsMeta.totalCount;
    }
  }

  return (
    <Box w="100%" px={{ base: 4, md: 10 }} pt={5}>
      <QuoteDashboardSummary
        meta={summaryMeta}
        pending={dashboardIsPending}
        error={dashboardError}
        onExpectedQuotes={() => showExpectedAction('quote_required')}
        onExpectedAction={showExpectedAction}
        onRetry={() => getDashboard(search, isFilterDisabled)}
      />
      <BaseTabs
        height="calc(100% - 36px)"
        index={tabIndex >= 0 ? tabIndex : 0}
        onChange={index => history.push(getQuotesPath(index))}
      >
        <SearchBar
          onSearch={applySearch}
          appliedSearch={search}
          meta={meta}
          pending={pending}
          quotesType={quotesType}
        />

        <TabList
          maxWidth="100%"
          overflowX={{ base: 'auto', lg: 'visible' }}
          overflowY={{ base: 'hidden', lg: 'visible' }}
        >
          {Object.entries(QUOTES_TYPES).map(([type, label]) => {
            const TabComponent = type === 'black' ? DiscardedQuotesTab : TabWithBadge;
            return (
              <TabComponent
                key={type}
                badgeBgColor={
                  type === 'green' ? 'red' : `tag.secondaryBg.${type === 'red' ? 'gray' : type}`
                }
                badgeContent={metaCountByType(type)}
              >
                {label}
              </TabComponent>
            );
          })}
        </TabList>

        <TabPanels id="quote-results" pt={8} pb={2}>
          {Object.keys(QUOTES_TYPES).map((type, index) => (
            <TabPanel key={type}>
              {index === (tabIndex >= 0 ? tabIndex : 0) && (
                <QuotesTable
                  quotes={quotes}
                  pending={pending}
                  error={error}
                  onRetry={() =>
                    getQuotes(currentPage, { ...search, status: quotesType }, isFilterDisabled)
                  }
                  refreshDashboard={() => getDashboard(search, isFilterDisabled)}
                  getQuotes={getQuotes}
                  updateQuotesComments={updateQuotesComments}
                  meta={meta}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  search={{ ...search, status: quotesType }}
                  isFilterDisabled={isFilterDisabled}
                />
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </BaseTabs>
    </Box>
  );
};

const mapStateToProps = ({ quotes: state }) => {
  return {
    pending: state.pending,
    error: state.error,
    quotes: state.data,
    meta: state.meta,
    dashboardMeta: state.dashboard.data,
    dashboardPending: state.dashboard.pending,
    dashboardError: state.dashboard.error,
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getQuotes: quotesActions.getQuotes,
      getDashboard: quotesActions.getDashboard,
      updateQuotesComments: quotesActions.updateQuotesComments,
    },
    dispatch,
  );

const toCompanyOption = company => ({
  label: company.name,
  value: company.id,
  departments: company.departments || [],
});

export const COMPANY_AUTOCOMPLETE_PAGE_SIZE = 2000;

export const loadCompanyOptions = inputValue =>
  companiesAPI
    .getCompanies(1, inputValue || '', { per_page: COMPANY_AUTOCOMPLETE_PAGE_SIZE })
    .then(data => (data.companies || []).map(toCompanyOption))
    .catch(() => []);

const toDepartmentOption = department => ({
  label: department.name ? `${department.code} - ${department.name}` : String(department),
  value: department.code || department.value || String(department),
});

const departmentCode = value =>
  String(value || '')
    .split(' - ', 1)[0]
    .trim()
    .toUpperCase();

export const departmentForCompany = (department, company) => {
  const companyDepartments = company?.departments || [];
  if (!department || companyDepartments.length === 0) return department;

  return companyDepartments.some(code => departmentCode(code) === departmentCode(department.value))
    ? department
    : null;
};

export const SearchBar = ({
  onSearch,
  appliedSearch = DEFAULT_SEARCH,
  quotesType,
  meta,
  pending,
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [currentTags, setCurrentTags] = useState([]);
  const [currentStages, setCurrentStages] = useState([]);
  const [currentCharacteristics, setCurrentCharacteristics] = useState([]);
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    setCurrentTags(appliedSearch.tags || []);
    setCurrentStages(
      (appliedSearch.stages || []).map(
        stage =>
          STAGE_FILTER_OPTIONS.find(
            option => stageFilterValue(option) === stageFilterValue(stage),
          ) || stage,
      ),
    );
    setCurrentCharacteristics(
      (appliedSearch.characteristics || []).map(
        value =>
          Object.keys(CHARACTERISTIC_FILTERS).find(
            label => CHARACTERISTIC_FILTERS[label] === value,
          ) || value,
      ),
    );
  }, [appliedSearch.tags, appliedSearch.stages, appliedSearch.characteristics]);
  const { user } = useSelector(state => state.authentication);
  const { isFilterDisabled } = useOperatorFilter();
  const isCompanyReferent = is(user, 'référent entreprise');
  const canFilterByDepartment =
    is(user, 'administrateur') || is(user, 'coordinateur entreprises') || isCompanyReferent;
  const exportHelp = 'L’export tient compte des filtres appliqués.';

  useEffect(() => {
    if (isCompanyReferent) {
      setAvailableTags([]);
    } else if (is(user, 'pilote externe') || is(user, 'référent externe')) {
      usersAPI
        .getUser(-1)
        .then(u => setAvailableTags(u.tags || []))
        .catch(() => setAvailableTags([]));
    } else {
      foldersAPI
        .getTags({ search: '' })
        .then(tags => setAvailableTags(Array.isArray(tags) ? tags : tags?.tags || []))
        .catch(() => setAvailableTags([]));
    }

    if (!canFilterByDepartment) {
      setDepartments([]);
      return;
    }

    departmentsAPI
      .getDepartments()
      .then(data => setDepartments((data.departments || data || []).map(toDepartmentOption)))
      .catch(() => setDepartments([]));
  }, [canFilterByDepartment, isCompanyReferent, user]);

  const submitSearch = values => {
    const stages = [...new Set(currentStages.map(stageFilterValue))];
    const nextPath = quotesPathForStageSearch(stages);

    onSearch({
      sort: sortOption(values.sort).value,
      keywords: values.search || '',
      delay: values.delay?.value || values.delay || '',
      tags: currentTags,
      stages,
      characteristics: currentCharacteristics.map(label => CHARACTERISTIC_FILTERS[label] || label),
      company: values.company || null,
      department: values.department || null,
    });

    if (nextPath && history.location.pathname !== nextPath) history.push(nextPath);
  };

  const openQuotesReport = () => {
    const query = {
      keywords: appliedSearch.keywords || '',
      delay: appliedSearch.delay?.value || appliedSearch.delay || '',
      company: appliedSearch.company?.value || appliedSearch.company || '',
      department: appliedSearch.department?.value || appliedSearch.department || '',
    };
    const url = `${
      process.env.REACT_APP_API_URL
    }/api/v1/quote_request_summaries/export.xlsx?by_keywords=${encodeURIComponent(
      query.keywords,
    )}&by_delay=${encodeURIComponent(query.delay)}&by_status=${quotesType}&by_company=${
      query.company
    }&by_department=${query.department}${encodeQueryArray(
      'by_tags',
      appliedSearch.tags || [],
    )}${encodeQueryArray('by_stages', appliedSearch.stages || [])}${encodeQueryArray(
      'by_characteristics',
      appliedSearch.characteristics || [],
    )}&ignore_operator_filter=${isFilterDisabled}&sort=${sortOption(appliedSearch.sort).value}`;
    window.open(url);
  };

  const initialValues = useMemo(
    () => ({
      sort: sortOption(appliedSearch.sort),
      search: appliedSearch.keywords || '',
      delay: appliedSearch.delay || '',
      company: appliedSearch.company,
      department: appliedSearch.department,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appliedSearch.keywords, appliedSearch.delay, appliedSearch.company, appliedSearch.department],
  );

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={submitSearch}>
      {formik => {
        const selectedCompany = formik.values.company;
        const companyDepartments = selectedCompany?.departments || [];
        const filteredDepartments = companyDepartments.length
          ? departments.filter(department =>
              companyDepartments.some(
                code =>
                  String(code)
                    .split(' - ', 1)[0]
                    .trim()
                    .toUpperCase() ===
                  String(department.value)
                    .trim()
                    .toUpperCase(),
              ),
            )
          : departments;

        const resetSearch = () => {
          formik.resetForm({
            values: {
              sort: SORT_OPTIONS[0],
              search: '',
              delay: '',
              company: null,
              department: null,
            },
          });
          setCurrentTags([]);
          setCurrentStages([]);
          setCurrentCharacteristics([]);
          onSearch(DEFAULT_SEARCH);
        };

        return (
          <Box
            as="form"
            data-testid="quote-search-panel"
            aria-label="Recherche et filtres"
            onSubmit={formik.handleSubmit}
            w="100%"
            bg="select.bg.isSelected"
            border="1px solid"
            borderColor="folderNav.borderColor.active"
            borderLeft="4px solid"
            borderLeftColor="red"
            rounded="lg"
            boxShadow="0 4px 10px rgba(35, 40, 45, 0.1)"
            px={{ base: 4, md: 5 }}
            py={4}
            mb={5}
          >
            <Heading as="h2" fontSize="md" color="red" mb={1}>
              Recherche et filtres
            </Heading>
            <Text fontSize="sm" color="semiDarkGray.600" mb={4}>
              Les filtres s’appliquent au tableau, aux indicateurs et à l’export.
            </Text>
            <Flex
              flexDirection={{ base: 'column', lg: 'row' }}
              alignItems={{ base: 'stretch', lg: 'flex-end' }}
              gridGap={4}
            >
              <Box flexGrow={1} minW={0} maxW={{ base: 'none', lg: '720px' }}>
                <Text
                  as="label"
                  htmlFor="quote-search"
                  d="block"
                  mb={1}
                  color="semiDarkGray.600"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Rechercher un dossier
                </Text>
                <SearchInput
                  id="quote-search"
                  name="search"
                  placeholder="Nom, prénom, n° de dossier, ville, département"
                  minW={0}
                  w="100%"
                  bg="lightGray"
                  border="1px solid"
                  borderColor="transparent"
                  _focus={{ borderColor: 'coral.900', bg: 'white' }}
                />
              </Box>
              <Flex
                flexWrap="wrap"
                alignItems="center"
                justifyContent={{ base: 'flex-start', lg: 'flex-end' }}
                gridGap={2}
                ml={{ base: 0, lg: 'auto' }}
              >
                <SecondaryButton accessibleCoral type="submit" size="sm">
                  Rechercher
                </SecondaryButton>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  variantColor="neutral"
                  color="semiDarkGray.900"
                  _hover={{ bg: 'lightGray3' }}
                  _active={{ bg: 'gray2' }}
                  onClick={resetSearch}
                >
                  Réinitialiser
                </Button>
                <Tooltip
                  label={exportHelp}
                  placement="top"
                  bg="darkGray"
                  color="white"
                  hasArrow
                  closeOnClick
                >
                  <Box as="span" display="inline-flex">
                    <PrimaryButton
                      accessibleCoral
                      type="button"
                      size="sm"
                      aria-describedby="quote-export-help"
                      onClick={openQuotesReport}
                      isDisabled={pending || meta?.total === 0}
                    >
                      Exporter Excel
                    </PrimaryButton>
                  </Box>
                </Tooltip>
                <Text
                  id="quote-export-help"
                  display={{ base: 'block', lg: 'none' }}
                  w="100%"
                  color="semiDarkGray.600"
                  fontSize="xs"
                >
                  {exportHelp}
                </Text>
              </Flex>
            </Flex>

            {!pending && meta?.total === 0 && (
              <Text role="status" mt={3} fontSize="sm" color="semiDarkGray.900">
                Aucun dossier ne correspond aux filtres. Modifiez-les pour exporter.
              </Text>
            )}

            <Box mt={4} pt={4} borderTop="1px solid" borderColor="lightGray3">
              <Text
                mb={3}
                color="semiDarkGray.900"
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="0.06em"
                textTransform="uppercase"
              >
                Affiner les résultats
              </Text>
              <Box
                data-testid="quote-filter-grid"
                display="grid"
                gridTemplateColumns={{
                  base: 'minmax(0, 1fr)',
                  md: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                }}
                gridGap={3}
              >
                <SelectInput
                  accessibleColors
                  name="company"
                  inputId="company"
                  label="Entreprise"
                  placeholder="Toutes les entreprises"
                  isAsync
                  defaultOptions
                  cacheOptions
                  loadOptions={loadCompanyOptions}
                  postOnChange={company =>
                    formik.setFieldValue(
                      'department',
                      departmentForCompany(formik.values.department, company),
                    )
                  }
                  styles={{ mb: 0, minW: 0, w: '100%' }}
                  size="sm"
                />
                {canFilterByDepartment && (
                  <SelectInput
                    accessibleColors
                    name="department"
                    inputId="department"
                    label="Département"
                    placeholder="Tous les départements"
                    options={filteredDepartments}
                    isClearable
                    styles={{ mb: 0, minW: 0, w: '100%' }}
                    size="sm"
                  />
                )}
                {!isCompanyReferent && (
                  <Box minW={0}>
                    <Label htmlFor="quote-tags-filter">Tag</Label>
                    <SelectInputMultiple
                      accessibleColors
                      id="quote-tags-filter"
                      styles={{ mb: 0, minW: 0, w: '100%', textTransform: 'uppercase' }}
                      options={availableTags}
                      menuOptionGroupOptions={{
                        onChange: setCurrentTags,
                        value: currentTags,
                        clear: () => setCurrentTags([]),
                      }}
                      label="Tous les tags"
                    />
                  </Box>
                )}
                {!isCompanyReferent && (
                  <SelectInput
                    accessibleColors
                    name="delay"
                    inputId="delay"
                    label="Délais"
                    isClearable
                    placeholder="Tous les délais"
                    styles={{ mb: 0, minW: 0, w: '100%' }}
                    size="sm"
                    options={[
                      { label: '< 15j', value: '< 15' },
                      { label: '15j < x < 45j', value: '15 < x < 45' },
                      { label: '> 45j', value: '> 45' },
                    ]}
                  />
                )}
                <Box minW={0}>
                  <Label htmlFor="quote-state-filter">État</Label>
                  <SelectInputMultiple
                    accessibleColors
                    id="quote-state-filter"
                    styles={{ mb: 0, minW: 0, w: '100%' }}
                    options={STAGE_FILTER_OPTIONS}
                    menuOptionGroupOptions={{
                      onChange: setCurrentStages,
                      value: currentStages,
                      clear: () => setCurrentStages([]),
                    }}
                    label="Tous les états"
                  />
                </Box>
                <Box minW={0}>
                  <Label htmlFor="quote-characteristics-filter">Caractéristiques</Label>
                  <SelectInputMultiple
                    accessibleColors
                    id="quote-characteristics-filter"
                    styles={{ mb: 0, minW: 0, w: '100%' }}
                    options={Object.keys(CHARACTERISTIC_FILTERS)}
                    menuOptionGroupOptions={{
                      onChange: setCurrentCharacteristics,
                      value: currentCharacteristics,
                      clear: () => setCurrentCharacteristics([]),
                    }}
                    label="Toutes les caractéristiques"
                  />
                </Box>
                <SelectInput
                  accessibleColors
                  name="sort"
                  inputId="sort"
                  label="Trier par"
                  options={SORT_OPTIONS}
                  postOnChange={option => {
                    if (option.value !== sortOption(appliedSearch.sort).value) {
                      onSearch({ ...appliedSearch, sort: option.value });
                    }
                  }}
                  styles={{ mb: 0, minW: 0, w: '100%' }}
                  size="sm"
                />
              </Box>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};

export const QuotesTable = ({
  quotes,
  pending,
  error = false,
  onRetry,
  refreshDashboard,
  getQuotes,
  updateQuotesComments,
  meta,
  currentPage,
  setCurrentPage,
  search,
  isFilterDisabled,
}) => {
  const { user } = useSelector(state => state.authentication);
  const hasQuotes = quotes && quotes.length > 0;
  const refreshQuotes = options => {
    const request = options
      ? getQuotes(currentPage, search, isFilterDisabled, options)
      : getQuotes(currentPage, search, isFilterDisabled);
    if (refreshDashboard) {
      Promise.resolve(request).then(
        () => refreshDashboard(),
        () => refreshDashboard(),
      );
    }
  };

  return (
    <>
      {error && hasQuotes && (
        <Flex role="alert" alignItems="center" justifyContent="center" gridGap={3} mb={3}>
          <Text color="coral.700">Impossible de charger les dossiers les plus récents.</Text>
          {onRetry && (
            <SecondaryButton accessibleCoral onClick={onRetry}>
              Réessayer les dossiers
            </SecondaryButton>
          )}
        </Flex>
      )}
      <Text
        fontSize="xs"
        color="semiDarkGray.600"
        textAlign="center"
        px={{ base: 4, md: 8 }}
        mb={2}
      >
        Chaque ligne correspond à une demande de devis ; un dossier peut apparaître plusieurs fois.
      </Text>
      <Box h="94%" overflowX="auto" overflowY="hidden">
        <Table
          d="flex"
          flexDirection="column"
          px={8}
          h="100%"
          minW={user.role === 'coordinateur entreprises' ? '1100px' : quoteTableMinWidth}
        >
          <Thead d="block" pos="sticky" zIndex="docked">
            <THeadRow
              d="grid"
              gridTemplateColumns={
                user.role === 'coordinateur entreprises'
                  ? quoteGridTemplateColumns + quoteTagColumn
                  : quoteGridTemplateColumns
              }
              w="100%"
            >
              <TH size="sm">
                Nom de
                <br />
                l'entreprise
              </TH>
              <TH size="sm">
                N° de
                <br />
                dossier
              </TH>
              <TH size="sm">Bénéficiaire</TH>
              <TH size="sm">Ville</TH>
              <TH size="sm">État</TH>
              <TH size="sm">
                <VisuallyHidden>Jalons</VisuallyHidden>
              </TH>
              <TH size="sm">Action requise</TH>
              {user.role === 'coordinateur entreprises' && <TH size="sm">Tag</TH>}
            </THeadRow>
          </Thead>
          <TBody overflowX="hidden" overflowY="auto" aria-busy={pending}>
            {error && !hasQuotes && (
              <TBodyRow role="alert" enableHover={false} d="block">
                <TD
                  colSpan={user.role === 'coordinateur entreprises' ? 8 : 7}
                  d="flex"
                  color="coral.700"
                  borderBottom={0}
                  h={40}
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gridGap={3}
                >
                  <Text>Impossible de charger les dossiers.</Text>
                  {onRetry && (
                    <SecondaryButton accessibleCoral onClick={onRetry}>
                      Réessayer les dossiers
                    </SecondaryButton>
                  )}
                </TD>
              </TBodyRow>
            )}
            {!error && !hasQuotes && (
              <TBodyRow enableHover={false} d="block">
                <TD
                  colSpan={user.role === 'coordinateur entreprises' ? 8 : 7}
                  d="flex"
                  color="semiDarkGray.600"
                  borderBottom={0}
                  h={40}
                  alignItems="center"
                  justifyContent="center"
                >
                  {pending ? <Spinner label="Chargement..." /> : 'Aucun résultat'}
                </TD>
              </TBodyRow>
            )}
            {hasQuotes &&
              quotes.map(quote => {
                const {
                  id,
                  companyName,
                  folderFormattedId,
                  recipientFullName,
                  folderId,
                  recipientHousingCity,
                  status,
                  dashboardStateLabel,
                  statusLabel,
                  statusColor,
                  depositsValidated,
                  actionRequired = [],
                  folderTag,
                } = quote;

                return (
                  <Funders
                    key={`${folderId}-${id || companyName}`}
                    folderId={folderId}
                    quote={quote}
                    canEditVisit={['référent entreprise', 'coordinateur entreprises'].includes(
                      user.role,
                    )}
                    onVisitUpdated={() => {
                      refreshQuotes();
                    }}
                    onQuoteUploaded={() => {
                      refreshQuotes();
                    }}
                    onDocumentsUpdated={() => {
                      refreshQuotes({ preserveData: true });
                    }}
                    onCommentsUpdated={updatedFolder => updateQuotesComments(id, updatedFolder)}
                    trigger={({ onClick, onActionClick, onAskQuestion }) => (
                      <TBodyRow
                        hasScaleOnHover={false}
                        d="grid"
                        gridTemplateColumns={
                          user.role === 'coordinateur entreprises'
                            ? quoteGridTemplateColumns + quoteTagColumn
                            : quoteGridTemplateColumns
                        }
                        w="100%"
                        cursor="pointer"
                        tabIndex={0}
                        aria-label={`Ouvrir le dossier ${folderFormattedId}`}
                        _hover={{ bg: 'lightGray', color: 'semiDarkGray.900' }}
                        _active={{ bg: 'select.bg.isSelected', color: 'semiDarkGray.900' }}
                        _focus={{ boxShadow: 'outline', color: 'semiDarkGray.900' }}
                        onClick={onClick}
                        onKeyDown={event => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key !== 'Enter' && event.key !== ' ') return;

                          event.preventDefault();
                          onClick();
                        }}
                      >
                        <TD hasPadding size="sm">
                          {companyName}
                        </TD>
                        <TD hasPadding size="sm">
                          <Button
                            type="button"
                            variant="link"
                            variantColor="neutral"
                            color="inherit"
                            fontWeight="semibold"
                            aria-label={`Ouvrir le dossier ${folderFormattedId}`}
                            onClick={event => {
                              event.stopPropagation();
                              onClick();
                            }}
                          >
                            {folderFormattedId}
                          </Button>
                        </TD>
                        <TD hasPadding size="sm">
                          {recipientFullName}
                        </TD>
                        <TD hasPadding size="sm" minW={0}>
                          <Text isTruncated title={recipientHousingCity}>
                            {recipientHousingCity}
                          </Text>
                        </TD>
                        <TD hasPadding size="sm">
                          <TypeTag
                            variantColor={statusColor || statusVariant(status)}
                            bg={
                              (statusColor || statusVariant(status)) === 'green'
                                ? '#EDF3F8'
                                : undefined
                            }
                            color="semiDarkGray.900"
                          >
                            {dashboardStateLabel || statusLabel || status || '—'}
                          </TypeTag>
                          <QuoteProgressSegments quote={quote} />
                          {quote.quoteFollowUp?.otherCompaniesCount > 0 && (
                            <Text mt={2} fontSize="xs" color="semiDarkGray.600">
                              Devis en cours avec {quote.quoteFollowUp.otherCompaniesCount} autre
                              {quote.quoteFollowUp.otherCompaniesCount > 1 ? 's' : ''} entreprise
                              {quote.quoteFollowUp.otherCompaniesCount > 1 ? 's' : ''} sur ce
                              dossier
                            </Text>
                          )}
                        </TD>
                        <TD hasPadding size="sm">
                          <QuoteMilestones quote={{ ...quote, depositsValidated }} />
                        </TD>
                        <TD hasPadding size="sm">
                          <QuoteActions
                            actions={
                              quote.quoteFollowUp
                                ? quote.quoteFollowUp.actions.map(action => action.label)
                                : actionRequired
                            }
                            details={quote.quoteFollowUp?.actions}
                            onAction={onActionClick}
                          />
                          {onAskQuestion && (
                            <Button
                              type="button"
                              variant="link"
                              variantColor="neutral"
                              size="xs"
                              display="block"
                              mt={2}
                              color="coral.700"
                              whiteSpace="normal"
                              textAlign="left"
                              onClick={event => {
                                event.stopPropagation();
                                onAskQuestion();
                              }}
                              onKeyDown={event => event.stopPropagation()}
                            >
                              Poser une question
                            </Button>
                          )}
                        </TD>
                        {user.role === 'coordinateur entreprises' && (
                          <TD hasPadding size="sm" textTransform="uppercase">
                            {folderTag || '—'}
                          </TD>
                        )}
                      </TBodyRow>
                    )}
                  />
                );
              })}
          </TBody>
        </Table>
      </Box>
      {meta && (
        <Pagination
          current={currentPage}
          total={meta.total}
          defaultPageSize={meta.perPage}
          onChange={page => setCurrentPage(page)}
        />
      )}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(QuotesTabs);
